import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Github, Calendar, User, Tag, ArrowRight } from 'lucide-react';
import Layout from '../layouts/Layout';
import apiClient from '../helpers/apiClient';

const PortfolioDetailPage = () => {
    const { slug } = useParams();
    const [portfolio, setPortfolio] = useState(null);
    const [relatedProjects, setRelatedProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchPortfolioItem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const fetchPortfolioItem = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await apiClient.getPortfolioItem(slug);
            setPortfolio(response.data);
            
            // Fetch related projects (same category, excluding current)
            const relatedResponse = await apiClient.getPortfolio();
            const related = relatedResponse.data
                .filter(item => item.category === response.data.category && item.slug !== slug)
                .slice(0, 3);
            setRelatedProjects(related);
            
        } catch (err) {
            setError(err.message || 'Failed to load portfolio item');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Layout title="Loading..." path={`/portfolio/${slug}`}>
                <div className="min-h-screen bg-white">
                    <div className="pt-32 pb-20 px-6">
                        <div className="max-w-6xl mx-auto">
                            {/* Loading skeleton */}
                            <div className="animate-pulse">
                                <div className="h-8 bg-slate-200 rounded w-1/4 mb-8"></div>
                                <div className="h-12 bg-slate-200 rounded w-3/4 mb-4"></div>
                                <div className="h-6 bg-slate-200 rounded w-1/2 mb-8"></div>
                                <div className="h-96 bg-slate-200 rounded mb-8"></div>
                                <div className="space-y-4">
                                    <div className="h-4 bg-slate-200 rounded"></div>
                                    <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                                    <div className="h-4 bg-slate-200 rounded w-4/6"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout title="Portfolio Item Not Found" path={`/portfolio/${slug}`}>
                <div className="min-h-screen bg-white">
                    <div className="pt-32 pb-20 px-6">
                        <div className="max-w-4xl mx-auto text-center">
                            <h1 className="text-4xl font-bold text-slate-900 mb-6">Portfolio Item Not Found</h1>
                            <p className="text-xl text-slate-600 mb-8">{error}</p>
                            <Link
                                to="/portfolio"
                                className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Portfolio
                            </Link>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!portfolio) return null;

    return (
        <Layout
            title={`${portfolio.title} - Portfolio`}
            description={portfolio.short_description}
            keywords={`${portfolio.title}, ${portfolio.category}, ${portfolio.technologies?.join(', ')}, KGC Innovations portfolio`}
            path={`/portfolio/${slug}`}
        >
            <div className="min-h-screen bg-white">
                {/* Navigation */}
                <section className="pt-32 pb-8 px-6">
                    <div className="max-w-6xl mx-auto">
                        <Link
                            to="/portfolio"
                            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium transition-colors mb-8"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Portfolio
                        </Link>
                    </div>
                </section>

                {/* Hero Section */}
                <section className="pb-16 px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full font-medium">
                                        {portfolio.category}
                                    </span>
                                    {portfolio.featured && (
                                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full font-medium">
                                            Featured Project
                                        </span>
                                    )}
                                </div>

                                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                    {portfolio.title}
                                </h1>

                                <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                                    {portfolio.short_description}
                                </p>

                                {/* Project Meta */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                                    {portfolio.client_name && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <User className="w-5 h-5 text-slate-600" />
                                            </div>
                                            <div>
                                                <div className="text-sm text-slate-500">Client</div>
                                                <div className="font-medium text-slate-900">{portfolio.client_name}</div>
                                            </div>
                                        </div>
                                    )}

                                    {portfolio.project_date && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <Calendar className="w-5 h-5 text-slate-600" />
                                            </div>
                                            <div>
                                                <div className="text-sm text-slate-500">Completed</div>
                                                <div className="font-medium text-slate-900">{formatDate(portfolio.project_date)}</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                            <Tag className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-slate-500">Category</div>
                                            <div className="font-medium text-slate-900">{portfolio.category}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-4">
                                    {portfolio.project_url && (
                                        <a
                                            href={portfolio.project_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            View Live Project
                                        </a>
                                    )}

                                    {portfolio.github_url && (
                                        <a
                                            href={portfolio.github_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 bg-slate-100 text-slate-900 px-6 py-3 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                                        >
                                            <Github className="w-4 h-4" />
                                            View Code
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Featured Image */}
                            <div className="lg:order-first">
                                {portfolio.featured_image ? (
                                    <img
                                        src={portfolio.featured_image}
                                        alt={portfolio.title}
                                        className="w-full h-auto rounded-2xl shadow-2xl"
                                        onError={(e) => {
                                            e.target.src = '/images/placeholder-project.jpg';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-96 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center">
                                        <div className="text-slate-400 text-center">
                                            <div className="text-6xl mb-4">🖼️</div>
                                            <div className="text-lg font-medium">Project Image</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Technologies */}
                {portfolio.technologies && portfolio.technologies.length > 0 && (
                    <section className="py-16 bg-slate-50">
                        <div className="max-w-6xl mx-auto px-6">
                            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Technologies Used</h2>
                            <div className="flex flex-wrap justify-center gap-3">
                                {portfolio.technologies.map((tech, index) => (
                                    <span
                                        key={index}
                                        className="px-4 py-2 bg-white text-slate-700 rounded-lg border border-slate-200 font-medium shadow-sm"
                                    >
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Project Description */}
                <section className="py-16 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        <h2 className="text-3xl font-bold text-slate-900 mb-8">Project Overview</h2>
                        {portfolio.full_description ? (
                            <div className="prose prose-slate max-w-none">
                                {portfolio.full_description.split('\n').map((paragraph, index) => (
                                    paragraph.trim() && (
                                        <p key={index} className="text-lg text-slate-600 leading-relaxed mb-6">
                                            {paragraph}
                                        </p>
                                    )
                                ))}
                            </div>
                        ) : (
                            <p className="text-lg text-slate-600 leading-relaxed">
                                {portfolio.short_description}
                            </p>
                        )}
                    </div>
                </section>

                {/* Gallery */}
                {portfolio.gallery_images && portfolio.gallery_images.length > 0 && (
                    <section className="py-16 bg-slate-50">
                        <div className="max-w-6xl mx-auto px-6">
                            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Project Gallery</h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {portfolio.gallery_images.map((image, index) => (
                                    <div key={index} className="group cursor-pointer">
                                        <img
                                            src={image}
                                            alt={`${portfolio.title} - ${index + 1}`}
                                            className="w-full h-64 object-cover rounded-xl shadow-lg group-hover:shadow-2xl transition-all group-hover:scale-105"
                                            onError={(e) => {
                                                e.target.src = '/images/placeholder-project.jpg';
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Related Projects */}
                {relatedProjects.length > 0 && (
                    <section className="py-16 bg-white">
                        <div className="max-w-6xl mx-auto px-6">
                            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Related Projects</h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {relatedProjects.map((project) => (
                                    <Link
                                        key={project.id}
                                        to={`/portfolio/${project.slug}`}
                                        className="group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                                    >
                                        <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                                            {project.featured_image ? (
                                                <img
                                                    src={project.featured_image}
                                                    alt={project.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                    onError={(e) => {
                                                        e.target.src = '/images/placeholder-project.jpg';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <div className="text-slate-400 text-4xl">🖼️</div>
                                                </div>
                                            )}
                                            {project.featured && (
                                                <div className="absolute top-4 left-4">
                                                    <span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded-full font-medium">
                                                        Featured
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                                                    {project.category}
                                                </span>
                                                {project.client_name && (
                                                    <span className="text-slate-500 text-xs">
                                                        • {project.client_name}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors">
                                                {project.title}
                                            </h3>
                                            <p className="text-slate-600 mb-4 line-clamp-3">
                                                {project.short_description}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex flex-wrap gap-1">
                                                    {project.technologies.slice(0, 3).map((tech, techIndex) => (
                                                        <span
                                                            key={techIndex}
                                                            className="px-2 py-1 bg-slate-50 text-slate-600 text-xs rounded font-medium"
                                                        >
                                                            {tech}
                                                        </span>
                                                    ))}
                                                    {project.technologies.length > 3 && (
                                                        <span className="px-2 py-1 bg-slate-50 text-slate-600 text-xs rounded font-medium">
                                                            +{project.technologies.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA Section */}
                <section className="py-16 bg-slate-900">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <h2 className="text-3xl font-bold text-white mb-6">
                            Ready to Start Your Project?
                        </h2>
                        <p className="text-xl text-slate-300 mb-8">
                            Let's discuss how we can bring your vision to life with cutting-edge technology and expert development.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/contact"
                                className="bg-white text-slate-900 px-8 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
                            >
                                Start Your Project
                            </Link>
                            <Link
                                to="/schedule-consultation"
                                className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-slate-900 transition-colors"
                            >
                                Schedule Consultation
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default PortfolioDetailPage;